import React, { useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../hooks/useOnSaved';
import { useTranslation } from 'react-i18next';
import * as uiSlice from '../../../data/ui.slice';
import * as clientSlice from '../../../data/client.slice';
import TextInput from '../../forms/TextInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Client } from '../../../data/models/client.model';
import { Program } from '../../../data/models/client-program.model';
import clientUtils from '../../../services/clientUtils';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'ProgramDialog';

const classes = {
  content: `${PREFIX}-content`,
  name: `${PREFIX}-name`,
  title: `${PREFIX}-title`,
  footer: `${PREFIX}-footer`,
  action: `${PREFIX}-action`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    position: 'absolute',
    top: 64,
  },
  '& .MuiButtonBase-root': {
    fontSize: '15px !important',
  },

  '& .MuiButton-root': {
    padding: theme.spacing(1, 1.75),
  },
  [`& .${classes.title}`]: {
    fontSize: '30px',
    fontWeight: 700,
    lineHeight: '40px',
    letterSpacing: '0.25px',
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: 380,
  },
  [`& .${classes.name}`]: {
    marginTop: theme.spacing(2),
  },
  [`& .${classes.footer}`]: {
    marginTop: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.action}`]: {
    letterSpacing: '0.46px',
    margin: 0,
    padding: '11px 20px',
    boxSizing: 'border-box',
  },
}));

interface ProgramDialogProps {
  title: string;
  open: boolean;
  client: Client;
  program?: Program;
  close: () => void;
  isDuplicate?: boolean;
}

export default function ProgramDialog({
  title,
  open,
  client,
  program,
  close,
  isDuplicate,
}: ProgramDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const navigate = useNavigate();
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const savingProgram = useAppSelector(clientSlice.selectSavingProgram);
  const programs = useAppSelector(clientSlice.selectPrograms);
  const savedProgram = useAppSelector(clientSlice.selectSavedProgram);

  const isViewOnly = useMemo(
    () => program && program.state === 'active' && !isDuplicate,
    [program, isDuplicate],
  );

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getProgramDefaults(program),
  });

  const handleClose = useCallback(() => {
    formHook.reset();
    close();
  }, [formHook, close]);

  React.useEffect(() => {
    const defaults = clientUtils.getProgramDefaults(program);
    formHook.reset(defaults);
  }, [program]);

  useOnSaved(savedProgram, () => {
    if (!open) return;
    handleClose();
    if (saveSuccessMessage === 'snackbar.taskComplete') {
      navigate(`/clients/${client.clientBC}/programs`);
    }
  });

  const handleSave = (data: any) => {
    const newProgram = clientUtils.getProgramFromFormData(
      client.clientBC,
      data,
    );
    if (program && !isDuplicate) {
      dispatch(clientSlice.updateClientProgramAsync(newProgram));
    } else {
      if (newProgram.programUUID) {
        delete newProgram.programUUID;
      }

      dispatch(
        clientSlice.createClientProgramAsync(newProgram, {
          isFirst: !programs.length,
        }),
      );
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <TextInput
            name="Name"
            label={t('client.programName')}
            className={classes.name}
            control={formHook.control}
            required={true}
            readOnly={isViewOnly}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            name="ProgramID"
            label={t('client.programID')}
            className={classes.name}
            control={formHook.control}
            required={true}
            readOnly={isViewOnly}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            name="Description"
            label={t('client.programDescription')}
            className={classes.name}
            control={formHook.control}
            required={true}
            readOnly={isViewOnly}
            rules={{ required: requiredFieldMessage }}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.footer}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClose}
          className={classes.action}>
          {t('common.cancel')}
        </Button>
        {!isViewOnly && (
          <Button
            variant="contained"
            color="primary"
            disabled={!formHook.formState.isValid || savingProgram}
            onClick={formHook.handleSubmit(handleSave)}
            className={classes.action}>
            {!!program ? t('common.save') : t('common.add')}
          </Button>
        )}
      </DialogActions>
    </RootDialog>
  );
}
